/**
 * ISSUE-1387. The conference picker on a college league's score list (v1:
 * scores league_filter_dropdown.dart, the chip's inner pill with a chevron).
 * A pill names the current choice, "All conferences" or one conference, and
 * opens the shared Dialog (a bottom sheet under 768px) holding the kit's
 * Radio group. A tap commits and closes, like a sort picker.
 *
 * The options are sports-api's (POST /league/conference-list); a league with
 * none draws nothing. The choice lives in `?conference=` and the open sheet
 * in `?sheet=conference`, never in component state.
 */
import { cn, Dialog, Radio } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { ChevronDownIcon } from "../../icons/glyphs";
import {
  OUTLINE_PILL_CLASS,
  OUTLINE_PILL_HEADER_TARGET_CLASS,
} from "../standings/StandingsLinkButton";

export const CONFERENCE_SHEET = "conference";
export const CONFERENCE_ALL_LABEL = "All conferences";
export const CONFERENCE_DIALOG_TITLE = "Conference";
export const CONFERENCE_ALL_VALUE = "all";

export type typeConferenceOption = { conferenceKey: string; label: string };

/** The pill's text: the chosen conference's label, or All conferences. */
export function conferencePillLabel(input: {
  conferences: readonly typeConferenceOption[];
  conferenceKey: string | null;
}): string {
  return (
    input.conferences.find(
      (conference) => conference.conferenceKey === input.conferenceKey,
    )?.label ?? CONFERENCE_ALL_LABEL
  );
}

/** "No SEC games for Saturday, Oct 3." */
export function conferenceEmptyLine(input: {
  label: string;
  selectionLabel: string;
}): string {
  return input.selectionLabel === ""
    ? `No ${input.label} games here yet.`
    : `No ${input.label} games for ${input.selectionLabel}.`;
}

export const ConferenceFilter = (props: {
  conferences: readonly typeConferenceOption[];
  /** The applied conference, or null for every game. */
  conferenceKey: string | null;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Undefined clears the filter. The caller applies the choice and closes
   *  the sheet in one navigation, so neither write can drop the other. */
  onSelect: (conferenceKey: string | undefined) => void;
}): ReactNode => {
  if (props.conferences.length === 0) return null;
  const label = conferencePillLabel(props);
  return (
    <>
      <button
        type="button"
        className={cn(OUTLINE_PILL_HEADER_TARGET_CLASS, "self-start")}
        data-testid="conference-filter-trigger"
        aria-haspopup="dialog"
        aria-label={`${CONFERENCE_DIALOG_TITLE}: ${label}`}
        onClick={props.onOpen}
      >
        <span className={OUTLINE_PILL_CLASS}>
          {label}
          <ChevronDownIcon />
        </span>
      </button>
      <Dialog
        open={props.isOpen}
        onOpenChange={(next) => {
          if (!next) props.onClose();
        }}
        title={CONFERENCE_DIALOG_TITLE}
      >
        <Radio
          label={CONFERENCE_DIALOG_TITLE}
          labelHidden
          items={[
            { value: CONFERENCE_ALL_VALUE, label: CONFERENCE_ALL_LABEL },
            ...props.conferences.map((conference) => ({
              value: conference.conferenceKey,
              label: conference.label,
            })),
          ]}
          value={props.conferenceKey ?? CONFERENCE_ALL_VALUE}
          onValueChange={(next) => {
            props.onSelect(next === CONFERENCE_ALL_VALUE ? undefined : next);
          }}
        />
      </Dialog>
    </>
  );
};
